import React from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard, Globe2, BookOpen, TrendingUp, Sunrise, PieChart, Settings,
  ChevronsLeft, ChevronsRight, Terminal, LogOut, LogIn,
} from "lucide-react";

const iconMap = { LayoutDashboard, Globe2, BookOpen, TrendingUp, Sunrise, PieChart, Settings };

const links = [
  { to: "/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/financial-journal", label: "Financial Journal", icon: "Globe2" },
  { to: "/morning-brief", label: "Morning Brief", icon: "Sunrise" },
  { to: "/stock-analysis", label: "Stock Analysis", icon: "TrendingUp" },
  { to: "/settings", label: "Settings", icon: "Settings" },
];

export const Sidebar = ({ collapsed, onToggle, onLogout, mobileOpen, onMobileClose, user }) => {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-mobile-overlay"
        />
      )}
      <aside
        data-testid="sidebar"
        className={[
          "fixed lg:sticky top-0 left-0 h-screen z-50 flex flex-col",
          "bg-fij-bg border-r border-fij-border transition-all duration-300",
          collapsed ? "w-16" : "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-3 h-14 border-b border-fij-border shrink-0">
          <Link
            to="/"
            data-testid="sidebar-brand-home"
            aria-label="FIJ Terminal — Home"
            onClick={onMobileClose}
            className="flex items-center gap-2 overflow-hidden hover:opacity-90 transition-opacity"
          >
            <div className="w-8 h-8 grid place-items-center bg-fij-green/10 border border-fij-green/30 text-fij-green rounded-sm shrink-0">
              <Terminal size={16} />
            </div>
            {!collapsed && (
              <div className="font-mono text-sm tracking-tight">
                <div className="text-fij-text leading-none">FIJ</div>
                <div className="text-fij-textMuted text-[10px] mt-0.5">v0.1 // TERMINAL</div>
              </div>
            )}
          </Link>
          <button
            data-testid="sidebar-toggle"
            onClick={onToggle}
            className="hidden lg:grid place-items-center w-7 h-7 rounded-sm text-fij-textDim hover:text-fij-green hover:bg-fij-surface transition-colors"
            aria-label="Toggle sidebar"
          >
            {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {links.map((l) => {
            const Icon = iconMap[l.icon];
            return (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={onMobileClose}
                data-testid={`sidebar-nav-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-2.5 py-2 rounded-sm text-sm transition-colors group relative",
                    isActive
                      ? "text-fij-green bg-fij-green/10"
                      : "text-fij-textDim hover:text-fij-text hover:bg-fij-surface",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-fij-green rounded-r" />}
                    <Icon size={16} className="shrink-0" />
                    {!collapsed && <span className="truncate font-mono">{l.label}</span>}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-fij-border p-2 shrink-0">
          {user ? (
            <button
              data-testid="sidebar-logout"
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-2.5 py-2 rounded-sm text-fij-textDim hover:text-fij-red hover:bg-fij-surface transition-colors text-sm font-mono"
            >
              <LogOut size={16} className="shrink-0" />
              {!collapsed && <span>Logout</span>}
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="sidebar-signin"
              onClick={onMobileClose}
              className="w-full flex items-center gap-3 px-2.5 py-2 rounded-sm text-fij-textDim hover:text-fij-green hover:bg-fij-surface transition-colors text-sm font-mono"
            >
              <LogIn size={16} className="shrink-0" />
              {!collapsed && <span>Sign In</span>}
            </Link>
          )}
          {!collapsed && (
            <div className="mt-2 px-2.5 py-2 font-mono text-[10px] text-fij-textMuted leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-fij-green animate-pulse-green" />
                <span>MARKETS // LIVE</span>
              </div>
              <div className="mt-1">UTC {new Date().toISOString().slice(11, 16)}</div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
